import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { getJson } from "@/lib/api";
import { Nav } from "./Nav";
import { Footer } from "./Footer";

const MAX_TRIES = 15;

const usePaymentStatus = (sessionId) => {
  const [state, setState] = useState(sessionId ? "checking" : "missing");
  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    let timer;
    let alive = true;
    const poll = async () => {
      tries += 1;
      try {
        const d = await getJson(`/payments/status/${encodeURIComponent(sessionId)}`);
        if (!alive) return;
        if (d?.payment_status === "paid") return setState("paid");
        if (d?.payment_status === "expired" || d?.payment_status === "failed") return setState("failed");
      } catch {
        if (!alive) return;
      }
      if (tries >= MAX_TRIES) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [sessionId]);
  return state;
};

const COPY = {
  checking: { icon: Loader2, spin: true, title: "Confirming your subscription…", text: "This only takes a moment." },
  paid: { icon: CheckCircle2, title: "Welcome to KeepThis!", text: "Your subscription is confirmed. A receipt from Stripe is on its way to your email." },
  failed: { icon: XCircle, title: "Payment didn't go through", text: "No charge was made. You can try again anytime." },
  timeout: { icon: Loader2, title: "Still confirming", text: "Your payment is taking longer than usual to confirm. Please check your email for a Stripe receipt." },
  missing: { icon: XCircle, title: "We couldn't find that checkout", text: "Please head back and try again." },
  cancel: { icon: XCircle, title: "Checkout canceled", text: "No charge was made. Your memories will wait for you." },
};

const ResultCard = ({ state }) => {
  const c = COPY[state];
  const Icon = c.icon;
  return (
    <div data-testid={`payment-result-${state}`} className="mx-auto max-w-lg rounded-[2rem] bg-white p-10 text-center shadow-[0_30px_80px_-35px_rgba(18,52,90,0.5)]">
      <Icon className={`mx-auto h-14 w-14 ${state === "paid" ? "text-teal" : "text-navy/70"} ${c.spin ? "animate-spin" : ""}`} aria-hidden="true" />
      <h1 className="mt-6 font-heading text-3xl font-black text-navy">{c.title}</h1>
      <p className="mt-3 text-base leading-relaxed text-navy/80">{c.text}</p>
      <Link to="/#pricing" data-testid="payment-back-home" className="mt-8 inline-flex h-12 items-center rounded-full bg-navy px-6 font-heading font-extrabold text-cream transition-[background-color] hover:bg-[#0c2644]">Back to KeepThis!</Link>
    </div>
  );
};

export default function PaymentResult({ canceled = false }) {
  const [params] = useSearchParams();
  const status = usePaymentStatus(canceled ? null : params.get("session_id"));
  return (
    <>
      <Nav />
      <main id="main" className="px-5 pb-24 pt-36 sm:px-8 lg:pt-44">
        <ResultCard state={canceled ? "cancel" : status} />
      </main>
      <Footer />
    </>
  );
}
